import { useEffect, useRef } from 'react';

/**
 * Keep keyboard focus inside an open dialog, and give it back to whatever opened it on close.
 *
 * <p><b>This is the client's first real focus trap</b>, added by feature 029 for the category
 * picker (FR-029). The strongest behaviour anywhere before it was "focus the primary button on
 * open" (`ConfirmDialog.tsx`), which leaves Tab free to walk out of the dialog and into the page
 * behind it — a keyboard user then finds themselves editing an auction they cannot see. No focus
 * library is installed (no Headless UI, no Radix) and none is being added for this; the hand-rolled
 * dialog is the house style, so this is the missing piece of it rather than a new dependency.</p>
 *
 * <p>Deliberately a generic utility with no knowledge of any feature, so the other dialogs can
 * adopt it later without inheriting anything of the picker's (Core Principle IV).</p>
 *
 * <p><b>What it does</b>, while `isActive`:</p>
 * <ul>
 *   <li>moves focus into the container on open — the first focusable control, or the container
 *       itself if it has none yet (a dialog that is still loading);</li>
 *   <li>wraps Tab at the last control and Shift+Tab at the first;</li>
 *   <li>restores focus to the element that was focused when the trap became active, so closing
 *       the picker puts the editor back on the button that opened it.</li>
 * </ul>
 *
 * <p><b>What it deliberately does not do:</b> it does not close on Escape (that is
 * `useEscapeClose`, already shared and already used by this page's popup) and it does not lock
 * body scroll — this route is viewport-locked already, and doing it here would surprise a caller
 * that is not.</p>
 *
 * <p>The focusable set is re-read on every Tab rather than cached, because a dialog's controls
 * change while it is open: the picker's Save is disabled during a save, and its "new category"
 * field appears only once that entry is chosen. A cached list would trap focus on a control that
 * is no longer there.</p>
 *
 * @param isActive whether the trap is running. Usually the dialog's `isOpen`.
 * @returns a ref to put on the dialog container.
 */
export function useFocusTrap<T extends HTMLElement = HTMLDivElement>(isActive: boolean) {
  const containerRef = useRef<T>(null);

  useEffect(() => {
    if (!isActive) return;

    const container = containerRef.current;
    if (!container) return;

    // Captured before focus moves, so it is the opener rather than whatever the trap focused.
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusable = (): HTMLElement[] =>
      Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true',
      );

    const initial = focusable();
    if (initial.length > 0) {
      initial[0]?.focus();
    } else {
      // No controls yet. Focus the container so the trap has somewhere to hold, and so a screen
      // reader announces the dialog rather than leaving the user on the page behind it.
      container.setAttribute('tabindex', '-1');
      container.focus();
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const elements = focusable();
      if (elements.length === 0) {
        // Nothing to move to, so Tab must not escape the dialog either.
        event.preventDefault();
        return;
      }

      const first = elements[0]!;
      const last = elements[elements.length - 1]!;
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey) {
        // Also catches focus sitting on the container itself, which is where it starts when the
        // dialog opened with no controls.
        if (active === first || active === container || !container.contains(active)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }

      if (active === last || !container.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      previouslyFocused?.focus?.();
    };
  }, [isActive]);

  return containerRef;
}

/**
 * Everything that can take focus. `[href]` rather than `a` so an anchor without one — which is not
 * focusable — is left out, and the negative `tabindex` exclusion keeps out elements a component has
 * deliberately removed from the tab order.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');
